import { FinancialDetails, MAX_IBAN_CHANGE_ATTEMPTS } from 'cpfr-common';
import { useAnalyticsActionTracker } from '@cp-shared-19/frontend-ui';
import { Hr } from '@vwfs-bronson/bronson-react';
import React, { useState } from 'react';
import { Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import { changeIbanPath } from '../../../../navigation/paths';
import { ConsultView } from './consult-view';
import { EditView } from './edit-view';
import { EditStatus } from './enums';
import { useDispatch } from 'react-redux';
import { ibanStatusTagFetchStateActions } from './consult-view/useIbanStatusTagSlice';
import { scrollToElement } from '../../../../../utils';

type IbanSectionProps = {
    details: FinancialDetails;
    isActive?: boolean;
    contractId: string;
    userEmail: string;
    isContactInformationChangePending: boolean;
    setShowSuccessNotification: (val: boolean) => void;
};

export const IbanSection: React.FC<IbanSectionProps> = ({
    details,
    isActive,
    contractId,
    userEmail,
    isContactInformationChangePending,
    setShowSuccessNotification,
}) => {
    const history = useHistory();
    const { path } = useRouteMatch();
    const location = useLocation<{ state_iban?: EditStatus }>();
    const { state_iban } = location.state || { state_iban: EditStatus.NOT_PERFORMED };
    const [lastEditStatus, setLastEditStatus] = useState<EditStatus | undefined>(state_iban);
    const [effectDate, setEffectDate] = useState<string>();
    const [attemptsCounter, setAttemptsCounter] = useState<number>(1);
    const { onAction } = useAnalyticsActionTracker('financialDetailsEditIbanSection');
    const { onAction: onEditSuccess } = useAnalyticsActionTracker('financialDetailsEditIbanSectionSuccess');
    const dispatch = useDispatch();

    if (!isActive || !details) {
        return null;
    }

    const isChangeEnabled = !!details._links?.changeIban;

    const startEditing = (): void => {
        if (isChangeEnabled) {
            setLastEditStatus(EditStatus.NOT_PERFORMED);
            onAction();
            history.push(changeIbanPath(contractId));
            setTimeout(() => {
                scrollToElement(`iban-section-${contractId}`);
            }, 100);
        }
    };

    const finishEditing = (lastEditStatus: EditStatus): void => {
        setLastEditStatus(lastEditStatus);
        history.replace(path, { state_iban: lastEditStatus });
        setTimeout(() => {
            scrollToElement(`iban-section-view-${contractId}`);
        }, 100);
    };

    const onSuccess = (lastEditStatus: EditStatus, effectDate?: string): void => {
        setAttemptsCounter((state) => state + 1);
        setEffectDate(effectDate);
        onEditSuccess();
        finishEditing(lastEditStatus);
        dispatch(ibanStatusTagFetchStateActions.setStatusTag(true));
        setShowSuccessNotification(true);
    };

    const onFail = (lastEditStatus: EditStatus): void => {
        setAttemptsCounter((state) => state + 1);
        if (attemptsCounter === MAX_IBAN_CHANGE_ATTEMPTS) {
            finishEditing(lastEditStatus);
        }
    };

    return (
        <>
            <Hr className={'u-mb-none'} />
            <Switch>
                {isChangeEnabled && (
                    <Route exact path={changeIbanPath(contractId)}>
                        <EditView
                            iban={details.iban}
                            onSuccess={onSuccess}
                            onFail={onFail}
                            contractId={contractId}
                            email={userEmail}
                            attempt={attemptsCounter}
                        />
                    </Route>
                )}
                <Route path={path}>
                    <ConsultView
                        details={details}
                        startEditing={startEditing}
                        lastEditStatus={lastEditStatus || EditStatus.NOT_PERFORMED}
                        isChangeEnabled={isChangeEnabled}
                        effectDate={effectDate}
                        disabledEdit={isContactInformationChangePending || attemptsCounter > MAX_IBAN_CHANGE_ATTEMPTS}
                    />
                </Route>
            </Switch>
        </>
    );
};
